import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, CalendarDays, Users, Sparkles, Ticket, Bell, BarChart3,
  LogOut, Trash2, Loader2, PawPrint, Database, Eye, X, MessageSquare, Phone, Mail, MapPin, Calendar, Clock
} from "lucide-react";
import { sendEmailNotification, buildStatusUpdateEmailTemplate } from "../lib/emailService";
import {
  getFirestoreCollection,
  updateFirestoreDoc,
  deleteFirestoreDoc,
  createFirestoreDoc,
  subscribeToCollection,
  adminFirebaseLogout,
  seedInitialServicesToFirebase,
  DEFAULT_WEBSITE_SERVICES,
} from "../lib/firebase";

const STATUSES = ["Pending", "Confirmed", "On the Way", "Completed", "Cancelled"];
const STATUS_COLOR = {
  Pending: "bg-amber-100 text-amber-700", Confirmed: "bg-sky-100 text-sky-700",
  "On the Way": "bg-purple-100 text-purple-700", Completed: "bg-green-100 text-green-700",
  Cancelled: "bg-red-100 text-red-600",
};
const NAV = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { id: "appointments", label: "Appointments", icon: CalendarDays },
  { id: "subscriptions", label: "Subscriptions", icon: Sparkles },
  { id: "customers", label: "Customers", icon: Users },
  { id: "services", label: "Services", icon: Sparkles },
  { id: "coupons", label: "Coupons", icon: Ticket },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [authed, setAuthed] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [messages, setMessages] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [newCoupon, setNewCoupon] = useState({ code: "", discount_percent: 10 });

  const logout = async () => {
    await adminFirebaseLogout();
    nav("/admin/login");
  };

  const loadAll = useCallback(async () => {
    // 1. Fetch Firestore Services safely
    let fbServices = [];
    try {
      fbServices = await getFirestoreCollection("services");
      if (!fbServices || fbServices.length === 0) {
        fbServices = await seedInitialServicesToFirebase();
      }
    } catch {
      fbServices = DEFAULT_WEBSITE_SERVICES;
    }

    // 2. Fetch other Firestore collections safely
    const fbBookings = await getFirestoreCollection("bookings").catch(() => []);
    const fbCustomers = await getFirestoreCollection("customers").catch(() => []);
    const fbCoupons = await getFirestoreCollection("coupons").catch(() => []);
    const fbMessages = await getFirestoreCollection("messages").catch(() => []);

    // 3. Optional Backend API fallback
    let apiBookings = [], apiCustomers = [], apiServices = [], apiCoupons = [], apiMessages = [], apiAnalytics = null;
    try {
      const [b, c, s, cp, m, a] = await Promise.all([
        api.get("/admin/bookings"), api.get("/admin/customers"), api.get("/services"),
        api.get("/admin/coupons"), api.get("/admin/messages"), api.get("/admin/analytics"),
      ]);
      apiBookings = b.data; apiCustomers = c.data; apiServices = s.data;
      apiCoupons = cp.data; apiMessages = m.data; apiAnalytics = a.data;
    } catch {}

    // Combine data & set state and deduplicate by service name
    const rawServicesList = (fbServices && fbServices.length > 0) ? fbServices : (apiServices && apiServices.length > 0 ? apiServices : DEFAULT_WEBSITE_SERVICES);
    const seenNames = new Set();
    const finalServices = rawServicesList
      .filter((s) => {
        const key = (s.name || s.title || "").toLowerCase().trim();
        if (!key || seenNames.has(key)) return false;
        seenNames.add(key);
        return true;
      })
      .map((s, idx) => ({
        id: s.id || `service-id-${idx}`,
        name: s.name || s.title || "Grooming Service",
        price: s.price || 999,
        category: s.category || "dog",
        duration: s.duration || "60 mins",
        summary: s.summary || s.tagline || s.desc || "Professional pet care",
        image: s.image || "https://images.unsplash.com/photo-1544568100-847a948585b9?auto=format&fit=crop&w=600&q=80",
        badge: s.badge || "Popular",
        ...s,
      }));

    setBookings(fbBookings.length > 0 ? fbBookings : apiBookings);
    setCustomers(fbCustomers.length > 0 ? fbCustomers : apiCustomers);
    setServices(finalServices);
    setCoupons(fbCoupons.length > 0 ? fbCoupons : apiCoupons);
    setMessages(fbMessages.length > 0 ? fbMessages : apiMessages);
    setAnalytics(apiAnalytics);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("woofsy_admin_token");
    if (!token) {
      setAuthed(false);
      nav("/admin/login");
      return;
    }
    setAuthed(true);
    loadAll();

    // Subscribe to real-time Firebase Firestore updates
    const unsubBookings = subscribeToCollection("bookings", (list) => {
      if (list && list.length > 0) setBookings(list);
    });
    const unsubCoupons = subscribeToCollection("coupons", (list) => {
      if (list && list.length > 0) setCoupons(list);
    });
    const unsubServices = subscribeToCollection("services", (list) => {
      if (list && list.length > 0) {
        const sNames = new Set();
        const dedupedList = list.filter((s) => {
          const key = (s.name || s.title || "").toLowerCase().trim();
          if (!key || sNames.has(key)) return false;
          sNames.add(key);
          return true;
        }).map((s, idx) => ({ id: s.id || `service-id-${idx}`, ...s }));
        setServices(dedupedList);
      }
    });

    return () => {
      unsubBookings();
      unsubCoupons();
      unsubServices();
    };
  }, [nav, loadAll]);

  const updateStatus = async (id, status) => {
    try {
      await updateFirestoreDoc("bookings", id, { status });
      await api.patch(`/admin/bookings/${id}`, { status }).catch(() => {});
      
      const targetBooking = bookings.find((b) => b.id === id);
      if (targetBooking && targetBooking.email) {
        const emailHtml = buildStatusUpdateEmailTemplate(targetBooking, status);
        sendEmailNotification({
          to: targetBooking.email,
          subject: `🐾 Woofsy Appointment Status Updated to: ${status}`,
          html: emailHtml,
        }).catch(() => {});
      }

      toast.success(`Booking status updated to ${status}! Email sent.`);
      loadAll();
    } catch {
      toast.error("Status update failed");
    }
  };

  const delBooking = async (id) => {
    try {
      await deleteFirestoreDoc("bookings", id);
      await api.delete(`/admin/bookings/${id}`).catch(() => {});
      toast.success("Firebase: Booking deleted!");
      loadAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  const addCoupon = async () => {
    if (!newCoupon.code) return toast.error("Enter a coupon code");
    try {
      await createFirestoreDoc("coupons", {
        code: newCoupon.code.toUpperCase(),
        discount_percent: Number(newCoupon.discount_percent),
      });
      await api.post("/admin/coupons", newCoupon).catch(() => {});
      toast.success("Firebase: Coupon created!");
      setNewCoupon({ code: "", discount_percent: 10 });
      loadAll();
    } catch {
      toast.error("Failed to create coupon");
    }
  };

  const delCoupon = async (id) => {
    try {
      await deleteFirestoreDoc("coupons", id);
      await api.delete(`/admin/coupons/${id}`).catch(() => {});
      toast.success("Firebase: Coupon deleted");
      loadAll();
    } catch {
      toast.error("Failed to delete coupon");
    }
  };

  if (authed === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-woofsy-primary" /></div>;

  const counts = STATUSES.reduce((o, s) => ({ ...o, [s]: bookings.filter((b) => b.status === s).length }), {});

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 bg-white border-r border-slate-100 p-5 hidden md:flex flex-col sticky top-0 h-screen">
        <div className="font-heading font-extrabold text-2xl text-woofsy-dark mb-8">🐾 Woof<span className="text-woofsy-primary">sy</span></div>
        <nav className="space-y-1 flex-1">
          {NAV.map((n) => (
            <button key={n.id} data-testid={`admin-nav-${n.id}`} onClick={() => setTab(n.id)}
              className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${tab === n.id ? "bg-woofsy-primary text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              <n.icon className="w-4 h-4" />{n.label}
            </button>
          ))}
        </nav>
        <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-red-500 hover:bg-red-50"><LogOut className="w-4 h-4" />Logout</button>
      </aside>

      <main className="flex-1 p-5 sm:p-8 overflow-x-hidden">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-woofsy-dark capitalize">{tab}</h1>
            <p className="text-slate-500 text-sm">Manage your Woofsy business</p>
          </div>
          <div className="md:hidden"><select value={tab} onChange={(e) => setTab(e.target.value)} className="rounded-xl border px-3 py-2 text-sm">{NAV.map((n) => <option key={n.id} value={n.id}>{n.label}</option>)}</select></div>
        </div>

        {tab === "dashboard" && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[["Total Bookings", bookings.length], ["Pending", counts.Pending], ["On the Way", counts["On the Way"]], ["Completed", counts.Completed]].map(([l, v], i) => (
                <motion.div key={l} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                  className="bg-white rounded-2xl border border-slate-100 p-5">
                  <p className="text-slate-500 text-sm">{l}</p>
                  <p className="font-heading font-bold text-3xl text-woofsy-dark mt-1">{v}</p>
                </motion.div>
              ))}
            </div>
            <div className="bg-white rounded-2xl border border-slate-100 p-5">
              <h3 className="font-heading font-semibold text-woofsy-dark mb-4">Recent Appointments</h3>
              <BookingTable bookings={bookings.slice(0, 5)} updateStatus={updateStatus} delBooking={delBooking} />
            </div>
          </div>
        )}

        {tab === "appointments" && (
          <div className="bg-white rounded-2xl border border-slate-100 p-5">
            <BookingTable bookings={bookings} updateStatus={updateStatus} delBooking={delBooking} />
          </div>
        )}

        {tab === "customers" && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    {["Owner Name", "Email", "Phone", "Pet Name", "Breed", "Address"].map((h) => (
                      <th key={h} className="text-left px-4 py-3 font-medium whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {customers.map((c, i) => (
                    <tr key={i} data-testid={`customer-row-${i}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="px-4 py-3 font-medium text-woofsy-dark">{c.name || c.owner_name || "—"}</td>
                      <td className="px-4 py-3 text-slate-500">{c.email || "—"}</td>
                      <td className="px-4 py-3 text-slate-500">{c.phone || "—"}</td>
                      <td className="px-4 py-3 text-slate-500">{c.pet_name || (Array.isArray(c.pets) ? c.pets.join(", ") : c.pets) || "—"}</td>
                      <td className="px-4 py-3 text-slate-500">{c.breed || "—"}</td>
                      <td className="px-4 py-3 text-slate-500 max-w-[180px] truncate" title={c.address}>{c.address || "—"}</td>
                    </tr>
                  ))}
                  {!customers.length && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No customers yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "subscriptions" && (
          <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-slate-100 p-4">
              <p className="text-sm text-slate-500 mb-1">Membership & Care Plan bookings — customers who booked a monthly, quarterly, or annual plan.</p>
            </div>
            <div className="bg-white rounded-2xl border border-slate-100 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    {["Owner", "Phone", "Email", "Plan Selected", "Amount", "Date", "Status", "Action"].map((h) => (
                      <th key={h} className="text-left px-3.5 py-3 font-semibold whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bookings
                    .filter((b) => {
                      const s = (b.service || "").toLowerCase();
                      return s.includes("membership") || s.includes("care plan") || s.includes("silver") || s.includes("gold") || s.includes("platinum") || s.includes("monthly") || s.includes("quarterly") || s.includes("annual") || s.includes("vip");
                    })
                    .map((b, i) => (
                      <tr key={b.id || i} className="border-t border-slate-100 hover:bg-slate-50/50">
                        <td className="px-3.5 py-3 font-medium text-woofsy-dark">
                          <div className="font-bold">{b.owner_name}</div>
                          <div className="text-xs text-slate-400">{b.pet_name} ({b.breed || "Dog"})</div>
                        </td>
                        <td className="px-3.5 py-3 text-woofsy-primary font-medium">{b.phone}</td>
                        <td className="px-3.5 py-3 text-slate-500 text-xs">{b.email}</td>
                        <td className="px-3.5 py-3 font-semibold text-slate-700">{b.service}</td>
                        <td className="px-3.5 py-3 font-extrabold text-woofsy-primary">₹{b.total_amount || b.price}</td>
                        <td className="px-3.5 py-3 text-slate-600 whitespace-nowrap">{b.preferred_date}</td>
                        <td className="px-3.5 py-3">
                          <select
                            value={b.status || "Pending"}
                            onChange={(e) => updateStatus(b.id, e.target.value)}
                            className={`rounded-full px-3 py-1 text-xs font-bold border-0 focus:outline-none cursor-pointer shadow-xs ${STATUS_COLOR[b.status] || STATUS_COLOR.Pending}`}
                          >
                            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                          </select>
                        </td>
                        <td className="px-3.5 py-3">
                          <button onClick={() => delBooking(b.id)} className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  {bookings.filter((b) => {
                    const s = (b.service || "").toLowerCase();
                    return s.includes("membership") || s.includes("care plan") || s.includes("silver") || s.includes("gold") || s.includes("platinum") || s.includes("monthly") || s.includes("quarterly") || s.includes("annual") || s.includes("vip");
                  }).length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No subscription bookings yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "services" && (
          <div className="space-y-6">
            {/* Header Actions: Seed Default Services */}
            <div className="flex flex-wrap items-center justify-between gap-4 bg-white rounded-2xl border border-slate-200/80 p-5 shadow-sm">
              <div>
                <h3 className="font-heading font-extrabold text-base text-woofsy-dark">Firebase Services Manager</h3>
                <p className="text-xs text-slate-500">Manage, edit prices, images and add new services to Firebase Firestore.</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={async () => {
                    try {
                      toast.info("Cleaning duplicate services in Firebase...");
                      const count = await cleanupDuplicateServicesInFirebase();
                      toast.success(count > 0 ? `Removed ${count} duplicate services!` : "No duplicates found in Firebase!");
                      loadAll();
                    } catch {
                      toast.error("Failed to clean duplicates");
                    }
                  }}
                  className="px-4 py-2.5 rounded-xl bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 font-bold text-xs shadow-xs transition-all flex items-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  <span>Remove Duplicates ({services.length})</span>
                </button>

                <button
                  onClick={async () => {
                    try {
                      toast.info("Seeding unique default services to Firebase...");
                      await seedInitialServicesToFirebase();
                      toast.success("All default website services synced into Firebase!");
                      loadAll();
                    } catch {
                      toast.error("Failed to seed services");
                    }
                  }}
                  className="px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs shadow transition-all flex items-center gap-2"
                >
                  <Database className="w-3.5 h-3.5 text-amber-400" />
                  <span>Sync Default Services</span>
                </button>
              </div>
            </div>

            {/* Create New Service Card */}
            <div className="bg-white rounded-2xl border border-slate-200/80 p-6 shadow-sm">
              <h3 className="font-heading font-extrabold text-lg text-woofsy-dark mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-woofsy-primary" />
                <span>Add / Create New Service in Firebase</span>
              </h3>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  const formEl = e.target;
                  const name = formEl.service_name.value.trim();
                  const price = Number(formEl.service_price.value);
                  const category = formEl.service_category.value;
                  const duration = formEl.service_duration.value || "60 mins";
                  const summary = formEl.service_summary.value || "Professional pet grooming service.";
                  const badge = formEl.service_badge.value || "Popular";
                  const image = formEl.service_image.value || "https://images.unsplash.com/photo-1544568100-847a948585b9?auto=format&fit=crop&w=600&q=80";

                  if (!name || !price) return toast.error("Please provide name and price");

                  try {
                    await createFirestoreDoc("services", {
                      name,
                      price,
                      category,
                      duration,
                      summary,
                      badge,
                      image,
                      inclusions: ["Professional Groomer", "Premium Bath & Shampoo", "Ear & Nail Care"],
                    });
                    toast.success("Firebase: Service created!");
                    formEl.reset();
                    loadAll();
                  } catch {
                    toast.error("Failed to create service");
                  }
                }}
                className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
              >
                <div>
                  <label className="text-xs text-slate-500 font-semibold block mb-1">Service Name *</label>
                  <input name="service_name" required className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="e.g. Royal Spa & Bath" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-semibold block mb-1">Price (₹) *</label>
                  <input name="service_price" type="number" required className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="1499" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-semibold block mb-1">Category</label>
                  <select name="service_category" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm">
                    <option value="dog">Dog Grooming 🐶</option>
                    <option value="addons">Add-On Services ✨</option>
                    <option value="membership">Membership Plans 👑</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-semibold block mb-1">Duration</label>
                  <input name="service_duration" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="45-60 mins" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs text-slate-500 font-semibold block mb-1">Summary / Tagline</label>
                  <input name="service_summary" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Brief tagline for service banner" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-semibold block mb-1">Badge Text</label>
                  <input name="service_badge" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="Best Seller ⭐" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 font-semibold block mb-1">Image URL</label>
                  <input name="service_image" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" placeholder="https://..." />
                </div>
                <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
                  <button type="submit" className="px-6 py-2.5 rounded-xl bg-woofsy-primary hover:bg-[#e8792c] text-white font-bold text-sm shadow-md shadow-orange-500/20 transition-all">
                    + Save Service to Firebase
                  </button>
                </div>
              </form>
            </div>

            {/* Services List Grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((s) => (
                <div key={s.id} className="bg-white rounded-2xl border border-slate-200/80 overflow-hidden shadow-sm hover:shadow-md transition-all p-5 flex flex-col justify-between">
                  <div>
                    {s.image && (
                      <div className="h-36 -mx-5 -mt-5 mb-4 overflow-hidden relative">
                        <img src={s.image} alt={s.name} className="w-full h-full object-cover" />
                        <span className="absolute top-2 right-2 text-xs font-bold px-2.5 py-1 rounded-full bg-white/90 text-slate-800 shadow">
                          {s.badge || s.category || "Service"}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <h3 className="font-heading font-extrabold text-base text-woofsy-dark">{s.name}</h3>
                      <span className="font-extrabold text-lg text-woofsy-primary shrink-0">₹{s.price}</span>
                    </div>
                    <p className="text-slate-500 text-xs line-clamp-2 mb-3">{s.summary || s.desc || "Professional pet grooming service."}</p>
                    <span className="inline-block text-[11px] font-semibold bg-slate-100 text-slate-600 rounded-full px-3 py-1 mb-4">
                      ⏱ {s.duration || "60 mins"}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-3 border-t border-slate-100 gap-2">
                    <button
                      onClick={async () => {
                        const newPrice = prompt(`Update price for ${s.name}:`, s.price);
                        if (newPrice !== null && !isNaN(Number(newPrice))) {
                          try {
                            await updateFirestoreDoc("services", s.id, { price: Number(newPrice) });
                            toast.success(`Price updated to ₹${newPrice}`);
                            loadAll();
                          } catch {
                            toast.error("Failed to update price");
                          }
                        }
                      }}
                      className="text-xs font-bold text-sky-600 hover:text-sky-700 underline"
                    >
                      Edit Price
                    </button>

                    <button
                      onClick={async () => {
                        if (confirm(`Delete service "${s.name}" from Firebase?`)) {
                          try {
                            await deleteFirestoreDoc("services", s.id);
                            toast.success("Service deleted from Firebase");
                            loadAll();
                          } catch {
                            toast.error("Failed to delete service");
                          }
                        }
                      }}
                      className="text-red-400 hover:text-red-600 flex items-center gap-1 text-xs font-medium"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              ))}
              {!services.length && (
                <p className="col-span-full p-8 text-center text-slate-400">No custom services in Firebase yet. Default services are displayed on the frontend.</p>
              )}
            </div>
          </div>
        )}

        {tab === "coupons" && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl border border-slate-100 p-5 flex flex-wrap items-end gap-3">
              <div><label className="text-xs text-slate-500 block mb-1">Code</label><input data-testid="coupon-code-input" value={newCoupon.code} onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })} className="rounded-xl border px-3 py-2 text-sm" placeholder="SUMMER25" /></div>
              <div><label className="text-xs text-slate-500 block mb-1">Discount %</label><input data-testid="coupon-discount-input" type="number" value={newCoupon.discount_percent} onChange={(e) => setNewCoupon({ ...newCoupon, discount_percent: +e.target.value })} className="rounded-xl border px-3 py-2 text-sm w-28" /></div>
              <button data-testid="add-coupon-btn" onClick={addCoupon} className="rounded-xl bg-woofsy-primary text-white px-5 py-2 text-sm font-medium">Add Coupon</button>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {coupons.map((c) => (
                <div key={c.id} className="bg-white rounded-2xl border border-dashed border-woofsy-primary/40 p-5 flex items-center justify-between">
                  <div><p className="font-heading font-bold text-lg text-woofsy-dark">{c.code}</p><p className="text-sm text-woofsy-primary">{c.discount_percent}% off</p></div>
                  <button onClick={() => delCoupon(c.id)} data-testid={`delete-coupon-${c.code}`} className="text-red-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "notifications" && (
          <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100">
            {messages.length ? messages.map((m, i) => (
              <div key={i} className="p-4 flex items-start gap-3"><Bell className="w-4 h-4 text-woofsy-primary mt-1" /><div><p className="font-medium text-woofsy-dark">{m.name} <span className="text-slate-400 text-xs">· {m.email}</span></p><p className="text-slate-500 text-sm">{m.message}</p></div></div>
            )) : <p className="p-8 text-center text-slate-400">No messages yet</p>}
          </div>
        )}

        {tab === "analytics" && analytics && (
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-slate-100 p-6">
              <h3 className="font-heading font-semibold text-woofsy-dark mb-4">Bookings by Status</h3>
              {STATUSES.map((s) => { const v = analytics.by_status?.[s] || 0; const max = Math.max(1, ...Object.values(analytics.by_status || {})); return (
                <div key={s} className="mb-3"><div className="flex justify-between text-sm mb-1"><span className="text-slate-500">{s}</span><span className="font-medium">{v}</span></div><div className="h-2 rounded-full bg-slate-100"><div className="h-full rounded-full bg-woofsy-primary" style={{ width: `${(v / max) * 100}%` }} /></div></div>
              ); })}
            </div>
            <div className="bg-white rounded-2xl border border-slate-100 p-6">
              <h3 className="font-heading font-semibold text-woofsy-dark mb-4">Popular Services</h3>
              {Object.entries(analytics.by_service || {}).length ? Object.entries(analytics.by_service).map(([s, v]) => { const max = Math.max(1, ...Object.values(analytics.by_service)); return (
                <div key={s} className="mb-3"><div className="flex justify-between text-sm mb-1"><span className="text-slate-500">{s}</span><span className="font-medium">{v}</span></div><div className="h-2 rounded-full bg-slate-100"><div className="h-full rounded-full bg-woofsy-accent" style={{ width: `${(v / max) * 100}%` }} /></div></div>
              ); }) : <p className="text-slate-400 text-sm">No data yet</p>}
              <div className="mt-6 pt-4 border-t border-slate-100 flex items-center gap-2 text-sm text-slate-500"><PawPrint className="w-4 h-4 text-woofsy-primary" /> {analytics.newsletter_subs} newsletter subscribers</div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function BookingTable({ bookings, updateStatus, delBooking }) {
  const [selectedBooking, setSelectedBooking] = useState(null);

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-slate-500 bg-slate-50/50">
            <tr>
              {["Pet Details", "Owner Info", "Service Selected", "Date & Slot", "Address", "Amount", "Status", "Actions"].map((h) => (
                <th key={h} className="text-left px-3.5 py-3 font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bookings.map((b, i) => (
              <tr key={b.id || i} data-testid={`booking-row-${i}`} className="border-t border-slate-100 hover:bg-slate-50/50 transition-colors">
                <td className="px-3.5 py-3 font-medium text-woofsy-dark whitespace-nowrap">
                  <div className="font-bold">{b.pet_name}</div>
                  <span className="text-xs text-slate-400">({b.breed || b.pet_type || "Dog"})</span>
                </td>
                <td className="px-3.5 py-3 text-slate-600 whitespace-nowrap">
                  <div className="font-bold text-slate-800">{b.owner_name}</div>
                  <div className="text-xs text-woofsy-primary font-medium">{b.phone}</div>
                  <div className="text-[11px] text-slate-400">{b.email}</div>
                </td>
                <td className="px-3.5 py-3 text-slate-700 font-semibold whitespace-nowrap">
                  {b.service || "Standard Grooming"}
                </td>
                <td className="px-3.5 py-3 text-slate-600 whitespace-nowrap">
                  <div className="font-semibold text-slate-900">{b.preferred_date}</div>
                  <span className="text-xs text-slate-500">{b.preferred_time || "Morning Slot"}</span>
                </td>
                <td className="px-3.5 py-3 text-slate-600 text-xs max-w-[180px] truncate" title={b.address}>
                  {b.address || "Pune"}
                </td>
                <td className="px-3.5 py-3 font-extrabold text-woofsy-primary whitespace-nowrap">
                  ₹{b.total_amount || b.price || 1399}
                </td>
                <td className="px-3.5 py-3">
                  <select
                    data-testid={`status-select-${i}`}
                    value={b.status || "Pending"}
                    onChange={(e) => updateStatus(b.id, e.target.value)}
                    className={`rounded-full px-3 py-1 text-xs font-bold border-0 focus:outline-none cursor-pointer shadow-xs ${STATUS_COLOR[b.status] || STATUS_COLOR.Pending}`}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </td>
                <td className="px-3.5 py-3">
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => setSelectedBooking(b)}
                      data-testid={`view-details-${i}`}
                      className="px-2.5 py-1.5 rounded-lg bg-woofsy-primary/10 text-woofsy-primary hover:bg-woofsy-primary hover:text-white transition-all font-semibold text-xs flex items-center gap-1"
                      title="View Details"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      <span>Details</span>
                    </button>
                    <button
                      onClick={() => delBooking(b.id)}
                      data-testid={`delete-booking-${i}`}
                      className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Delete Booking"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!bookings.length && (
              <tr>
                <td colSpan={8} className="px-3 py-10 text-center text-slate-400">
                  No appointments booked yet. Fill the booking form on main site to test!
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Booking Details Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl relative animate-in fade-in zoom-in duration-200">
            <button
              onClick={() => setSelectedBooking(null)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-woofsy-primary/10 text-woofsy-primary flex items-center justify-center font-bold text-xl">
                🐾
              </div>
              <div>
                <h3 className="font-heading font-extrabold text-xl text-woofsy-dark">Booking Details</h3>
                <p className="text-xs text-slate-400">ID: {selectedBooking.id || 'N/A'}</p>
              </div>
            </div>

            <div className="space-y-4 text-sm text-slate-700 bg-slate-50/70 rounded-2xl p-4 border border-slate-100">
              <div className="grid grid-cols-2 gap-3 border-b border-slate-200/60 pb-3">
                <div>
                  <span className="text-xs font-semibold text-slate-400 block uppercase">Pet Name</span>
                  <span className="font-bold text-slate-900 text-base">{selectedBooking.pet_name}</span>
                </div>
                <div>
                  <span className="text-xs font-semibold text-slate-400 block uppercase">Pet Category/Breed</span>
                  <span className="font-bold text-slate-900">{selectedBooking.pet_type || 'Dog'} ({selectedBooking.breed || 'N/A'})</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 border-b border-slate-200/60 pb-3">
                <div>
                  <span className="text-xs font-semibold text-slate-400 block uppercase">Owner Name</span>
                  <span className="font-bold text-slate-900">{selectedBooking.owner_name}</span>
                </div>
                <div>
                  <span className="text-xs font-semibold text-slate-400 block uppercase">Contact Phone</span>
                  <a href={`tel:${selectedBooking.phone}`} className="font-bold text-woofsy-primary flex items-center gap-1 hover:underline">
                    <Phone className="w-3.5 h-3.5" />
                    {selectedBooking.phone}
                  </a>
                </div>
              </div>

              <div className="border-b border-slate-200/60 pb-3">
                <span className="text-xs font-semibold text-slate-400 block uppercase">Owner Email</span>
                <a href={`mailto:${selectedBooking.email}`} className="font-bold text-slate-800 flex items-center gap-1 hover:underline">
                  <Mail className="w-3.5 h-3.5 text-slate-400" />
                  {selectedBooking.email}
                </a>
              </div>

              <div className="grid grid-cols-2 gap-3 border-b border-slate-200/60 pb-3">
                <div>
                  <span className="text-xs font-semibold text-slate-400 block uppercase">Service Required</span>
                  <span className="font-extrabold text-woofsy-primary">{selectedBooking.service}</span>
                </div>
                <div>
                  <span className="text-xs font-semibold text-slate-400 block uppercase">Total Amount</span>
                  <span className="font-extrabold text-woofsy-dark text-base">₹{selectedBooking.total_amount || selectedBooking.price || 1399}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 border-b border-slate-200/60 pb-3">
                <div>
                  <span className="text-xs font-semibold text-slate-400 block uppercase">Preferred Date</span>
                  <span className="font-semibold text-slate-800 flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5 text-woofsy-primary" />
                    {selectedBooking.preferred_date}
                  </span>
                </div>
                <div>
                  <span className="text-xs font-semibold text-slate-400 block uppercase">Time Slot</span>
                  <span className="font-semibold text-slate-800 flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5 text-woofsy-primary" />
                    {selectedBooking.preferred_time || 'Morning Slot'}
                  </span>
                </div>
              </div>

              <div className="border-b border-slate-200/60 pb-3">
                <span className="text-xs font-semibold text-slate-400 block uppercase">Address</span>
                <span className="font-medium text-slate-800 flex items-start gap-1">
                  <MapPin className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                  {selectedBooking.address}
                </span>
              </div>

              {selectedBooking.special_instructions && (
                <div>
                  <span className="text-xs font-semibold text-slate-400 block uppercase">Special Instructions</span>
                  <span className="font-medium text-slate-700 italic flex items-start gap-1">
                    <MessageSquare className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    {selectedBooking.special_instructions}
                  </span>
                </div>
              )}
            </div>

            <div className="mt-6 flex items-center justify-between gap-3">
              <a
                href={`https://api.whatsapp.com/send/?phone=918421114134&text=${encodeURIComponent(`Hi ${selectedBooking.owner_name}, regarding your Woofsy appointment for ${selectedBooking.pet_name}`)}&type=phone_number&app_absent=0`}
                target="_blank"
                rel="noreferrer"
                className="flex-1 py-3 px-4 rounded-xl bg-[#25D366] text-white font-bold text-xs text-center shadow-xs hover:bg-[#20bd5a] transition-colors flex items-center justify-center gap-2"
              >
                <span>💬 Chat on WhatsApp</span>
              </a>
              <button
                onClick={() => setSelectedBooking(null)}
                className="py-3 px-6 rounded-xl bg-slate-100 text-slate-600 font-bold text-xs hover:bg-slate-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
